import {
  colors,
  ImpAccountStatus,
  ImpButton,
  ImpContainer,
  ImpSpacer,
  ImpText,
  MaxWidth,
} from '@imprint-payments/imprint-ui';
import { useEffect } from 'react';

import { EVENT } from '../../analytics/analyticsConsts';
import { submitApplication } from '../../api/api';
import { useResendOtp } from '../../api/login/useLogin';
import { API_RESPONSE_CODE } from '../../api/responseVals';
import { useVerifyOtp } from '../../api/verifyOtp.api';
import {
  MODAL_ENUM,
  requireFullSSN,
  setLoading,
  setShowLinkHHScreen,
  setShowModal,
} from '../../app/applicationScreenSlice';
import { useAppSelector, useImpState } from '../../app/hooks';
import { setMultiCardUser } from '../../app/loginSlice';
import {
  setIncorrectOtpCode,
  setReachedMaxRetries,
  wrongOtpCode,
} from '../../app/otpScreenSlice';
import { SCREEN, setScreen } from '../../app/screenStateSlice';
import { applicationStatus, productID } from '../../assets/consts/const';
import { HeaderHandler } from '../../components/HeaderHandler/HeaderHandler';
import LinkHHAccount from '../../components/linkHHAccount/LinkHHAccount';
import Modal from '../../components/modal/Modal';
import WaterfallModal from '../../components/modal/WaterfallModal';
import OtpCardNew from '../../components/otp/OtpCardNew';
import { ANALYTICS } from '../../firebase/firebase';
import useNextPage from '../../hooks/nextPage';
import { formatPhoneNumber } from '../../utils/formatUtils';
import { clearHomePageState, getOfferUUIDs } from '../../utils/functionUtils';
import { merchantInfoByProductUUID } from '../../utils/productUUIDConstants';
import { PAGE } from '../../utils/routeConstant';
import {
  emitSDKEvent,
  emitSDKResult,
  isSDK,
  SDK_EVENTS,
  useSDKContext,
} from '../../utils/sdk';
import { SESSION_STORAGE } from '../../utils/sessionStorageConstants';
import UnfreezeCreditModal from '../otp/UnfreezeCreditModal';
import styles from './OtpVerify.module.css';

export default function OtpVerify() {
  const nextPage = useNextPage();
  const {
    language,
    merchantData,
    screenState,
    consumerInfo,
    dispatch,
    navigate,
    appScreenState,
    appStatus,
  } = useImpState();
  const multiCardUser = useAppSelector(
    (state) => state.loginSelector.multiCardUser
  );

  useEffect(() => {
    nextPage();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [screenState.screen]);

  const mutation = useVerifyOtp();

  const { result, setResult } = useSDKContext();

  const submitHandler = (code: string) => {
    mutation.mutate(
      { code },
      {
        onSuccess: () => {
          dispatch(wrongOtpCode(false));
          dispatch(setIncorrectOtpCode(false));
          if (
            merchantData.rewardDetails.merchantName &&
            merchantData.productUUID === productID.horizonHobby &&
            !appScreenState.eligibilityVerified
          ) {
            dispatch(setShowLinkHHScreen(true));
            dispatch(setLoading(false));
            return;
          }

          sendSubmitApplicationRequest();
        },
        onError: (err) => {
          if (err.code === API_RESPONSE_CODE.INCORRECT_AUTH) {
            dispatch(setIncorrectOtpCode(true));
            dispatch(wrongOtpCode(true));
            dispatch(setLoading(false));
            dispatch(setScreen(SCREEN.OTPSCREEN));
          }

          if (err.code === API_RESPONSE_CODE.EXPIRED_AUTH) {
            dispatch(setReachedMaxRetries(true));
            dispatch(setLoading(false));
            dispatch(setScreen(SCREEN.STATUS_OTP_VERIFICATION_FAILED));
          }
        },
      }
    );
  };

  const sendSubmitApplicationRequest = () => {
    dispatch(
      submitApplication({
        rewardsInfo: consumerInfo.rewardsInfo,
        consumer: consumerInfo.consumerProfile,
        consumerFactor: consumerInfo.consumerFactor,
        productUUID: merchantData.productUUID,
        isMultiCard: multiCardUser,
        offerUUIDs: getOfferUUIDs(),
        setResult,
        SSADisclosureAccepted:
          sessionStorage.getItem(SESSION_STORAGE.SSA_DISCLOSURE_ACCEPTED) ===
          'true',
      })
    );
  };

  function backClick() {
    clearHomePageState(dispatch);
    navigate(PAGE.HOME);
  }

  function loadDeeplink() {
    window.open(merchantData.deeplink);
  }

  useEffect(() => {
    window.history.pushState(
      'fake-route',
      document.title,
      window.location.href
    );
    // eslint-disable-next-line no-restricted-globals
    addEventListener('popstate', backClick);
    return () => {
      // eslint-disable-next-line no-restricted-globals
      removeEventListener('popstate', backClick);
      if (window.history.state === 'fake-route') {
        window.history.back();
      }
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const resendOtpMutation = useResendOtp();
  const resendHandler = () => {
    resendOtpMutation.mutate({ phone: consumerInfo.consumerProfile.phone });
  };

  const handleViewDeclineDetailsClick = () => {
    ANALYTICS.logEvent(EVENT.APP_DECLINE_LINK_CLICKED, {
      link: 'view_decline_details',
    });
    if (appStatus.rejectionReasonsURL !== '')
      window.open(appStatus.rejectionReasonsURL);
  };

  const handleGoBackClick = () => {
    clearHomePageState(dispatch);
  };

  const handleHowToUnfreezeModal = () => {
    dispatch(setShowModal(MODAL_ENUM.showHowToUnfreezeModal));
  };

  function getOTPSubline() {
    const phone = consumerInfo.consumerProfile.phone;
    const string_arr = language.otpSubline.split('%s');
    return `${string_arr[0]}${formatPhoneNumber(phone)}${string_arr[1]}`;
  }

  const getButtonState = (loading: boolean) => {
    if (loading) {
      return 'loading';
    } else {
      return 'enabled';
    }
  };

  const renderNotApprovedBody = () => {
    return (
      <ImpContainer textAlign="left">
        <ImpSpacer height="xl" />
        <ImpText typography="body2" color={colors.content.onSurfaceVariant}>
          {language.applicationNotApprovedBody.replace(
            '$cardName$',
            merchantData.rewardDetails.cardName
          )}
        </ImpText>
      </ImpContainer>
    );
  };

  const handleApplyAgain = () => {
    dispatch(setScreen(SCREEN.APPLICATION));
    navigate(PAGE.HOME);
    if (appStatus.reApplicationInApp) {
      dispatch(requireFullSSN(false));
      dispatch(setMultiCardUser(false));
    }
    ANALYTICS.logEvent(
      EVENT.APPLICATION_EVALUATION_DECISION_REJECTED_ACTION_CLICKED,
      {
        product_uuid: merchantData.productUUID,
      }
    );
  };

  const renderDeclineDetailsButton = () => {
    return (
      <ImpButton
        variant={isSDK() ? 'primary' : 'tertiary'}
        buttonStyle="branded"
        size="m"
        state={getButtonState(appScreenState.loading)}
        onClick={() => handleViewDeclineDetailsClick()}
      >
        {language.viewDeclineDetailsBtn}
      </ImpButton>
    );
  };

  const renderApplyAgainButton = () => {
    return (
      <ImpButton
        variant={isSDK() ? 'secondary' : 'primary'}
        buttonStyle="branded"
        size="m"
        state={getButtonState(appScreenState.loading)}
        onClick={() => handleApplyAgain()}
      >
        {language.applyAgain}
      </ImpButton>
    );
  };

  const renderNotApprovedButtons = () => {
    return (
      <>
        {isSDK() ? (
          <>
            <ImpSpacer height="xl" />
            {renderDeclineDetailsButton()}
            {appStatus.evaluationRejectReapply && (
              <>
                <ImpSpacer height="s" />
                {renderApplyAgainButton()}
              </>
            )}
            <ImpSpacer height="s" />
            <ImpButton
              variant="tertiary"
              buttonStyle="branded"
              state={getButtonState(appScreenState.loading)}
              onClick={() => {
                emitSDKResult({
                  result,
                });
                emitSDKEvent({
                  event: SDK_EVENTS.CLOSED,
                });
              }}
            >
              {language.backTo + merchantData.rewardDetails.merchantName}
            </ImpButton>
          </>
        ) : (
          <>
            <ImpSpacer height="xl" />
            {renderDeclineDetailsButton()}
            {appStatus.evaluationRejectReapply && (
              <>
                <ImpSpacer height="s" />
                {renderApplyAgainButton()}
              </>
            )}
          </>
        )}
      </>
    );
  };

  const getApplicationNotApprovedView = () => {
    if (appStatus.showCounterOffer) {
      return (
        <WaterfallModal
          handleViewDeclineDetailsClick={handleViewDeclineDetailsClick}
          showApplyAgainButton={appStatus.evaluationRejectReapply}
          handleApplyAgainClick={handleApplyAgain}
        />
      );
    } else {
      return (
        <ImpAccountStatus
          header={language.applicationNotApprovedTitle}
          body1={renderNotApprovedBody()}
          buttons={renderNotApprovedButtons()}
          showMobileDivider
          width={`min(${MaxWidth.Button}, 100vw - 32px)`}
        />
      );
    }
  };

  const renderStatusModal = (status: any) => {
    switch (status) {
      case applicationStatus.creditFrozen: {
        ANALYTICS.logEvent(EVENT.FROZEN_CREDIT_VIEWED);
        return (
          <Modal
            header={language.afterOtpAppStatusFrozenCreditHeader}
            body1={language.afterOtpAppStatusFrozenCreditBody}
            link
            linkText={language.afterOtpAppStatusFrozenCreditBtn}
            linkClick={() => {
              ANALYTICS.logEvent(EVENT.FROZEN_CREDIT_INFO_EDU_VIEWED);
              handleHowToUnfreezeModal();
            }}
            pillBtn1
          />
        );
      }
      case applicationStatus.inReview: {
        ANALYTICS.logEvent(EVENT.MANUAL_REVIEW_VIEWED);
        return (
          <Modal
            header={language.afterOtpAppStatusinReviewHeader}
            body1={language.afterOtpAppStatusInReviewBody}
            qrMessage={language.qrDownload.replace(
              '$link$',
              merchantInfoByProductUUID[merchantData.productUUID]
                .displaySignupLink
            )}
            showQR
            qr={merchantData.confirmLaterQR}
            buttonName1={language.downloadBtn}
            button1Click={loadDeeplink}
            pillBtn1
            hideMobileBtn1
            includeSupportEmail
          />
        );
      }
      case applicationStatus.notApproved: {
        ANALYTICS.logEvent(EVENT.APP_DECLINE_VIEWED);
        return getApplicationNotApprovedView();
      }
      case applicationStatus.asyncApplication: {
        return (
          <Modal
            header={language.afterOtpAppStatusAsyncApplicationCreditHeader}
            body1={language.afterOtpAppStatusAsyncApplicationCreditBody}
            includeSupportEmail
            pillBtn1
            delay={10000}
          />
        );
      }
      default: {
        return (
          <Modal
            header={language.appStatusFailedHeader}
            body1={language.appStatusFailedBody}
            buttonName1={language.gotItBtn}
            button1Click={handleGoBackClick}
            pillBtn1
          />
        );
      }
    }
  };

  if (language === null) {
    // eslint-disable-next-line react/jsx-no-useless-fragment
    return <></>;
  }

  return (
    <div className={styles.container}>
      <HeaderHandler />
      <div
        className={
          appStatus.status !== '' || appScreenState.showLinkHHScreen
            ? styles.hide
            : styles.cardContainer
        }
      >
        <OtpCardNew
          title={language.otpTitle}
          onFull={submitHandler}
          handleResend={resendHandler}
          bottomSubline={getOTPSubline()}
        />
      </div>
      {appStatus.status !== '' ? renderStatusModal(appStatus.status) : ''}

      {appScreenState.showModal === MODAL_ENUM.showHowToUnfreezeModal && (
        <UnfreezeCreditModal />
      )}

      {appScreenState.showLinkHHScreen && appStatus.status === '' && (
        <>
          <LinkHHAccount />
        </>
      )}
    </div>
  );
}
